/**
 * Classes Data Hooks — classService 실제 API 연동
 */
import { useQuery, useMutation, useQueryClient, type QueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import {
    getClassListByOrganization,
    getOpenClassList,
    getEnrolledClassList,
    fetchStudentMyClassesMerged,
    getClassDetail,
    getClassUserList,
    removeClassParticipant,
    getOrderUserCount,
    createClass,
    updateClass,
    deleteClass,
    assignClassDevices,
    unassignClassDevices,
    type GetClassListByOrganizationParams,
    type GetOpenClassListParams,
    type GetEnrolledClassListParams,
} from '../../services/classService';
import type {
    GetClassListByOrganizationResponseDto,
    GetOpenClassListResponseDto,
    GetEnrolledClassListResponseDto,
    GetClassDetailResponseDto,
    GetClassUserListResponseDto,
    RemoveClassParticipantResponseDto,
    GetOrderUserCountResponseDto,
    CreateClassRequestDto,
    UpdateClassRequestDto,
} from '../../types/api/class';

export const classKeys = {
    all: ['class'] as const,
    lists: () => [...classKeys.all, 'list'] as const,
    list: (params: object) => [...classKeys.lists(), params] as const,
    details: () => [...classKeys.all, 'detail'] as const,
    detail: (id: number) => [...classKeys.details(), id] as const,
    users: () => [...classKeys.all, 'users'] as const,
    userList: (id: number, params: object) => [...classKeys.users(), id, params] as const,
    orderUserCount: (orderId: number, productPriceId: number) => [...classKeys.all, 'order-user-count', orderId, productPriceId] as const,
    openList: (params: object) => [...classKeys.all, 'open-list', params] as const,
    enrolledLists: () => [...classKeys.all, 'enrolled-list'] as const,
    enrolledList: (params: GetEnrolledClassListParams) => [...classKeys.enrolledLists(), params] as const,
    studentMyClassesMerged: (page: number, pageSize: number) =>
        [...classKeys.enrolledLists(), 'merged-student', page, pageSize] as const,
};

// ─── 기관별 클래스 목록 ────────────────────────────────────────────────────────

export const useClassListByOrganization = (
    params: GetClassListByOrganizationParams,
    options?: Omit<UseQueryOptions<GetClassListByOrganizationResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    // undefined 값을 명시적으로 포함하여 쿼리 키가 확실히 변경되도록 함
    const queryKey = classKeys.list({
        ...params,
        language,
        // undefined 값도 명시적으로 포함
        organizationId: params.organizationId ?? null,
        isOpenClass: params.isOpenClass ?? null,
        isActive: params.isActive ?? null,
        title: params.title ?? null,
    });
    return useQuery({
        queryKey,
        queryFn: () => getClassListByOrganization({ ...params, language }),
        staleTime: 0,
        gcTime: 0, // 캐시 비활성화
        refetchOnMount: 'always', // 항상 재요청
        refetchOnWindowFocus: false,
        enabled: options?.enabled !== false,
        ...options,
    });
};

// ─── 오픈클래스 공개 목록: GET /class/open/list 만 (랜딩·/open-class-list·/master/open-class-list) ─

export const useOpenClassList = (
    params: GetOpenClassListParams,
    options?: Omit<UseQueryOptions<GetOpenClassListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: classKeys.openList({ ...params, language }),
        queryFn: () => getOpenClassList({ ...params, language }),
        staleTime: 30_000,
        refetchOnMount: true,
        enabled: options?.enabled !== false,
        ...options,
    });
};

// ─── 내 수강 클래스 목록 (GET /class/enrolled/list) ───────────────────────────

export const useEnrolledClassList = (
    params: GetEnrolledClassListParams,
    options?: Omit<UseQueryOptions<GetEnrolledClassListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    // queryKey를 명시적으로 구성하여 status 변경이 확실히 감지되도록
    const queryKey = [
        ...classKeys.enrolledLists(),
        params.page,
        params.pageSize,
        params.status ?? 'all',  // undefined 대신 'all' 문자열 사용
        language,
    ];
    return useQuery({
        queryKey,
        queryFn: () => getEnrolledClassList({ ...params, language }),
        staleTime: 0,  // 캐시 비활성화 - 필터 변경 시 즉시 재요청
        refetchOnMount: true,
        enabled: options?.enabled !== false,
        ...options,
    });
};

/** 수강생 마이클래스: 전 상태 enrolled + 인증 오픈목록 isEnrolled 병합 */
export const useStudentMyClassesList = (
    params: { page: number; pageSize: number },
    options?: Omit<UseQueryOptions<GetEnrolledClassListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const { page, pageSize } = params;
    const language = useApiLanguage();
    return useQuery({
        queryKey: [...classKeys.studentMyClassesMerged(page, pageSize), language],
        queryFn: () => fetchStudentMyClassesMerged({ page, pageSize, language }),
        staleTime: 30_000,
        refetchOnMount: true,
        enabled: options?.enabled !== false,
        ...options,
    });
};

/** 수강신청·초대 수락 등 이후 마이클래스 목록 캐시 무효화 */
export function invalidateEnrolledClassListQueries(queryClient: QueryClient): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: classKeys.enrolledLists() });
}

// ─── 클래스 상세 ──────────────────────────────────────────────────────────────

export const useClassDetail = (
    classId: number | undefined,
    options?: Omit<UseQueryOptions<GetClassDetailResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: [
            ...classKeys.detail(classId ?? 0),
            language,
        ],
        queryFn: ({ signal }) =>
            getClassDetail(classId!, {
                signal,
                language,
            }),
        enabled: classId != null && classId > 0,
        /** 0이면 마운트마다 재요청이 겹치며 늦은 응답이 캐시를 덮어 세션·동영상이 깜빡일 수 있음 */
        staleTime: 30 * 1000,
        refetchOnMount: true,
        ...options,
    });
};

export const useClassUserList = (
    classId: number | undefined,
    params: { page?: number; pageSize?: number } = {},
    options?: Omit<UseQueryOptions<GetClassUserListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: classKeys.userList(classId ?? 0, params),
        queryFn: () => getClassUserList(classId!, params),
        enabled: classId != null && classId > 0,
        staleTime: 0,
        refetchOnMount: true,
        ...options,
    });
};

/** PATCH /class/{classId}/user/{userId}/remove — 참가자 제외 후 목록·상세 캐시 무효화 */
export const useRemoveClassParticipant = () => {
    const queryClient = useQueryClient();
    return useMutation<
        RemoveClassParticipantResponseDto,
        Error,
        { classId: number; userId: number }
    >({
        mutationFn: ({ classId, userId }) => removeClassParticipant(classId, userId),
        onSuccess: (_data, { classId }) => {
            queryClient.invalidateQueries({ queryKey: [...classKeys.users(), classId], exact: false });
            queryClient.invalidateQueries({ queryKey: classKeys.detail(classId) });
        },
    });
};

export const useOrderUserCount = (
    orderId: number | undefined,
    productPriceId: number | undefined,
    options?: Omit<UseQueryOptions<GetOrderUserCountResponseDto>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: classKeys.orderUserCount(orderId ?? 0, productPriceId ?? 0),
        queryFn: () => getOrderUserCount(orderId!, productPriceId!),
        enabled: orderId != null && orderId > 0 && productPriceId != null && productPriceId > 0,
        staleTime: 0,
        refetchOnMount: true,
        ...options,
    });
};

// ─── 클래스 생성 ──────────────────────────────────────────────────────────────

export const useCreateClass = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateClassRequestDto) => createClass(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: classKeys.lists() });
        },
    });
};

// ─── 클래스 수정 ──────────────────────────────────────────────────────────────

export const useUpdateClass = (classId?: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: UpdateClassRequestDto) => {
            if (!classId) throw new Error('classId가 필요합니다.');
            return updateClass(classId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: classKeys.lists() });
            if (classId && classId > 0) {
                queryClient.invalidateQueries({ queryKey: classKeys.detail(classId) });
            }
        },
    });
};

// ─── 클래스 삭제 ──────────────────────────────────────────────────────────────

export const useDeleteClass = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (classId: number) => deleteClass(classId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: classKeys.lists() });
        },
    });
};

/** POST /class/{classId}/devices (body: { deviceIds }) — 클래스에 기관 기기(복수) 연결 */
export const useRegisterClassDevice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (args: { classId: number; organizationDeviceIds: number[]; organizationId: number }) =>
            assignClassDevices(args.classId, args.organizationDeviceIds),
        onSuccess: (_d, { organizationId }) => {
            queryClient.invalidateQueries({ queryKey: classKeys.lists() });
            if (organizationId > 0) {
                queryClient.invalidateQueries({ queryKey: ['device', 'org', organizationId, 'device-allocation'] });
            }
        },
    });
};

/** DELETE /class/{classId}/devices — 클래스에서 기관 기기 연결 해제 */
export const useUnassignClassDevice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (args: { classId: number; organizationDeviceIds: number[]; organizationId: number }) =>
            unassignClassDevices(args.classId, args.organizationDeviceIds),
        onSuccess: (_d, { organizationId }) => {
            queryClient.invalidateQueries({ queryKey: classKeys.lists() });
            if (organizationId > 0) {
                queryClient.invalidateQueries({ queryKey: ['device', 'org', organizationId, 'device-allocation'] });
            }
        },
    });
};

// ─── 하위 호환용 레거시 훅 (mock 의존 제거) ─────────────────────────────────────

/** @deprecated useClassListByOrganization 사용 권장 */
export const useClasses = (_organizationId?: string | null) => {
    return { classes: {}, loading: false, error: null };
};

/** @deprecated useClassDetail 사용 권장 */
export const useClass = (_classId?: string, _organizationId?: string | null) => {
    return { classData: null, loading: false, error: null };
};

/** @deprecated useClassListByOrganization 사용 권장 */
export const useClassesList = (_organizationId?: string | null) => {
    return { classesList: [], loading: false, error: null };
};
